import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import { 
  User, 
  FileText, 
  CreditCard, 
  CheckCircle, 
  Clock, 
  AlertCircle,
  ArrowRight,
  GraduationCap,
  Award,
  Download
} from 'lucide-react';

const StudentDashboard = () => {
  const { user } = useAuth();
  const [studentData, setStudentData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [offerLetterLoading, setOfferLetterLoading] = useState(false);

  useEffect(() => {
    const fetchStudentData = async () => {
      try {

        setLoading(true);
        // Fetch student profile, payment history, and status
        const [profileResponse, paymentResponse, statusResponse] = await Promise.all([
          axios.get('/api/students/profile').catch(err => ({ data: null, status: err.response?.status })),
          axios.get('/api/payments/history').catch(err => ({ data: null })),
          axios.get('/api/students/status').catch(err => ({ data: null }))
        ]);
        
        // Combine the data
        const profileData = profileResponse.data;
        const paymentData = paymentResponse.data;
        const statusData = statusResponse.data;
        
        if (profileData) {
          // Merge payment and status data into profile data
          const combinedData = {
            ...profileData,
            paymentStatus: paymentData?.paymentStatus || profileData.paymentStatus || 'pending',
            paymentReceipt: paymentData?.paymentReceipt || profileData.paymentReceipt,
            feeAmount: paymentData?.feeAmount,
            rank: statusData?.rank || profileData.rank
          };
          setStudentData(combinedData);
        } else {
          setStudentData(null);
        }
      } catch (error) {
        console.error('Error fetching student data:', error);
        setStudentData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchStudentData();
  }, []);

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'text-success-600 bg-success-100';
      case 'allocated':
        return 'text-warning-600 bg-warning-100';
      case 'accepted':
        return 'text-primary-600 bg-primary-100';
      case 'pending':
        return 'text-gray-600 bg-gray-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-5 w-5" />;
      case 'allocated':
        return <Award className="h-5 w-5" />;
      case 'accepted':
        return <CheckCircle className="h-5 w-5" />;
      case 'pending':
        return <Clock className="h-5 w-5" />;
      default:
        return <AlertCircle className="h-5 w-5" />;
    }
  };

  const handleViewOfferLetter = async () => {
    
    try {
      setOfferLetterLoading(true);
      const response = await axios.get('/api/students/offer-letter');
      // const newTab = window.open("","_blank");
      if (response.data.offerLetterUrl) {
        // Open Cloudinary URL in new window
        // newTab.location.href = response.data.offerLetterUrl;
        window.open(response.data.offerLetterUrl, '_blank');
      } else {
        // newTab.close();
        console.error('Offer letter URL not found');
      }
      
    } catch (error) {
      console.error('Error viewing offer letter:', error);
    } finally {
      setOfferLetterLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Welcome Section */}
      <div className="bg-gradient-to-r from-primary-600 to-primary-800 rounded-lg p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold mb-2">Welcome back, {user?.name}!</h1>
            <p className="text-primary-100">
              Track your admission progress and manage your application
            </p>
          </div>
          <div className="h-16 w-16 bg-white/10 rounded-full flex items-center justify-center">
            <User className="h-8 w-8" />
          </div>
        </div>
      </div>

             {/* Status Overview */}
       {studentData ? (
         <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
           <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
             <div className="flex items-center justify-between">
               <div>
                 <p className="text-sm font-medium text-gray-600">Application Status</p>
                 <p className={`text-lg font-semibold ${getStatusColor(studentData?.status || 'pending')} px-2 py-1 rounded-full inline-flex items-center mt-2`}>
                   {getStatusIcon(studentData?.status || 'pending')}
                   <span className="ml-1 capitalize">{studentData?.status || 'Not Started'}</span>
                 </p>
               </div>
               <FileText className="h-8 w-8 text-primary-600" />
             </div>
           </div>

           <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
             <div className="flex items-center justify-between">
               <div>
                 <p className="text-sm font-medium text-gray-600">Allocated Branch</p>
                 <p className="text-lg font-semibold text-gray-900 mt-2">
                   {studentData?.allocatedBranch || 'Not Allocated'}
                 </p>
               </div>
               <GraduationCap className="h-8 w-8 text-primary-600" />
             </div>
           </div>

           <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
             <div className="flex items-center justify-between">
               <div>
                 <p className="text-sm font-medium text-gray-600">Rank</p>
                 <p className="text-lg font-semibold text-gray-900 mt-2">
                   {studentData?.rank ? `Rank #${studentData.rank}` : 'Calculating...'}
                 </p>
                 {!studentData?.rank && (
                   <p className="text-xs text-gray-500 mt-1">
                     Rank will be calculated based on 12th marks
                   </p>
                 )}
               </div>
               <Award className="h-8 w-8 text-primary-600" />
             </div>
           </div>

           <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
             <div className="flex items-center justify-between">
               <div>
                 <p className="text-sm font-medium text-gray-600">Payment Status</p>
                 <p className={`text-lg font-semibold ${getStatusColor(studentData?.paymentStatus || 'pending')} px-2 py-1 rounded-full inline-flex items-center mt-2`}>
                   {getStatusIcon(studentData?.paymentStatus || 'pending')}
                   <span className="ml-1 capitalize">{studentData?.paymentStatus || 'Pending'}</span>
                 </p>
                 {studentData?.feeAmount && (
                   <p className="text-xs text-gray-500 mt-1">
                     Fee: ₹{studentData.feeAmount.toLocaleString()}
                   </p>
                 )}
               </div>
               <CreditCard className="h-8 w-8 text-primary-600" />
             </div>
           </div>
         </div>
       ) : (
         <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
           <div className="flex items-center">
             <AlertCircle className="h-8 w-8 text-yellow-600 mr-4" />
             <div>
               <h3 className="text-lg font-semibold text-yellow-800">Profile Not Created</h3>
               <p className="text-yellow-700 mt-1">
                 You haven't created your student profile yet. Please complete your profile to start your admission process.
               </p>
             </div>
           </div>
         </div>
       )}

       {/* Debug Info - Remove this in production */}
       {studentData && (
         <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
           <h3 className="text-sm font-medium text-gray-700 mb-2">Debug Information:</h3>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
               <div><strong>Status:</strong> {studentData.status || 'N/A'}</div>
               <div><strong>Payment Status:</strong> {studentData.paymentStatus || 'N/A'}</div>
               <div><strong>Rank:</strong> {studentData.rank ? `#${studentData.rank}` : 'N/A'}</div>
               <div><strong>Branch:</strong> {studentData.allocatedBranch || 'N/A'}</div>
               <div><strong>Fee Amount:</strong> {studentData.feeAmount ? `₹${studentData.feeAmount.toLocaleString()}` : 'N/A'}</div>
               <div><strong>Has Receipt:</strong> {studentData.paymentReceipt ? 'Yes' : 'No'}</div>
             </div>
         </div>
       )}

             {/* Quick Actions */}
       <div className="bg-white rounded-lg shadow-sm border border-gray-200">
         <div className="p-6 border-b border-gray-200">
           <h2 className="text-lg font-semibold text-gray-900">
             {studentData ? 'Quick Actions' : 'Get Started'}
           </h2>
         </div>
         <div className="p-6">
           <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
             <Link
               to="/student/profile"
               className={`flex items-center p-4 border rounded-lg transition-colors ${
                 studentData 
                   ? 'border-gray-200 hover:border-primary-300 hover:bg-primary-50' 
                   : 'border-primary-300 bg-primary-50 hover:bg-primary-100'
               }`}
             >
               <User className="h-6 w-6 text-primary-600 mr-3" />
               <div className="flex-1">
                 <h3 className="font-medium text-gray-900">
                   {studentData ? 'Update Profile' : 'Complete Profile'}
                 </h3>
                 <p className="text-sm text-gray-600">
                   {studentData ? 'Update personal and academic details' : 'Start by creating your student profile'}
                 </p>
               </div>
               <ArrowRight className="h-5 w-5 text-gray-400" />
             </Link>

             {studentData && (
               <>
                 <Link
                   to="/student/status"
                   className="flex items-center p-4 border border-gray-200 rounded-lg hover:border-primary-300 hover:bg-primary-50 transition-colors"
                 >
                   <FileText className="h-6 w-6 text-primary-600 mr-3" />
                   <div className="flex-1">
                     <h3 className="font-medium text-gray-900">Check Status</h3>
                     <p className="text-sm text-gray-600">View application progress</p>
                   </div>
                   <ArrowRight className="h-5 w-5 text-gray-400" />
                 </Link>

                 <Link
                   to="/student/payment"
                   className="flex items-center p-4 border border-gray-200 rounded-lg hover:border-primary-300 hover:bg-primary-50 transition-colors"
                 >
                   <CreditCard className="h-6 w-6 text-primary-600 mr-3" />
                   <div className="flex-1">
                     <h3 className="font-medium text-gray-900">Payment</h3>
                     <p className="text-sm text-gray-600">Upload payment receipt</p>
                   </div>
                   <ArrowRight className="h-5 w-5 text-gray-400" />
                 </Link>

                 {/* Offer Letter Section */}
                 {studentData.offerLetter && studentData.offerLetter.generated && (
                   <div className="flex items-center p-4 border border-blue-200 rounded-lg bg-blue-50">
                     <FileText className="h-6 w-6 text-blue-600 mr-3" />
                     <div className="flex-1">
                       <h3 className="font-medium text-blue-900">Offer Letter</h3>
                       <p className="text-sm text-blue-700">
                         Letter #{studentData.offerLetter.letterNumber}
                       </p>
                       <p className="text-xs text-blue-600">
                         Generated: {new Date(studentData.offerLetter.generatedAt).toLocaleDateString()}
                       </p>
                     </div>
                     <button
                       onClick={handleViewOfferLetter}
                       disabled={offerLetterLoading}
                       className="flex items-center text-blue-600 hover:text-blue-700 disabled:opacity-50"
                     >
                       <Download className="h-5 w-5 mr-1" />
                       {offerLetterLoading ? 'Loading...' : 'Download'}
                     </button>
                   </div>
                 )}
               </>
             )}
           </div>
         </div>
       </div>

             {/* Academic Summary */}
       {studentData && studentData.highSchoolMarks && (
         <div className="bg-white rounded-lg shadow-sm border border-gray-200">
           <div className="p-6 border-b border-gray-200">
             <h2 className="text-lg font-semibold text-gray-900">Academic Summary</h2>
           </div>
           <div className="p-6">
             <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
               <div>
                 <h3 className="font-medium text-gray-900 mb-4">High School Marks</h3>
                 <div className="space-y-3">
                   <div className="flex justify-between">
                     <span className="text-gray-600">Mathematics</span>
                     <span className="font-medium">{studentData.highSchoolMarks?.math || 0}/100</span>
                   </div>
                   <div className="flex justify-between">
                     <span className="text-gray-600">Science</span>
                     <span className="font-medium">{studentData.highSchoolMarks?.science || 0}/100</span>
                   </div>
                   <div className="flex justify-between">
                     <span className="text-gray-600">English</span>
                     <span className="font-medium">{studentData.highSchoolMarks?.english || 0}/100</span>
                   </div>
                   <div className="flex justify-between">
                     <span className="text-gray-600">Hindi</span>
                     <span className="font-medium">{studentData.highSchoolMarks?.hindi || 0}/100</span>
                   </div>
                   <div className="border-t pt-2 flex justify-between font-semibold">
                     <span>Total</span>
                     <span>{studentData.highSchoolMarks?.total || 0}/400</span>
                   </div>
                 </div>
               </div>

               <div>
                 <h3 className="font-medium text-gray-900 mb-4">12th Standard Marks</h3>
                 <div className="space-y-3">
                   <div className="flex justify-between">
                     <span className="text-gray-600">Physics</span>
                     <span className="font-medium">{studentData.twelfthMarks?.physics || 0}/100</span>
                   </div>
                   <div className="flex justify-between">
                     <span className="text-gray-600">Chemistry</span>
                     <span className="font-medium">{studentData.twelfthMarks?.chemistry || 0}/100</span>
                   </div>
                   <div className="flex justify-between">
                     <span className="text-gray-600">Mathematics</span>
                     <span className="font-medium">{studentData.twelfthMarks?.mathematics || 0}/100</span>
                   </div>
                   <div className="border-t pt-2 flex justify-between font-semibold">
                     <span>Total</span>
                     <span>{studentData.twelfthMarks?.total || 0}/300</span>
                   </div>
                 </div>
               </div>
             </div>
           </div>
         </div>
       )}
    </div>
  );
};

export default StudentDashboard;
